import { useState } from "react";
import { Reveal, Eyebrow } from "../components/primitives";
import CTABand from "../components/CTABand";
import VideoTestimonial from "../components/VideoTestimonial";
import { CLIENT_CATEGORIES, MIDDLE_EAST_CATEGORIES, BUILT_FROM_ZERO_CLIENTS, FEATURED_IN, TESTIMONIALS, VIDEO_TESTIMONIALS } from "../data/content";

const INITIAL_VISIBLE = 8;

function BrandGrid({ brands }) {
  const [open, setOpen] = useState(false);
  const shown = open ? brands : brands.slice(0, INITIAL_VISIBLE);
  return (
    <>
      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 border-t border-l border-taupe">
        {shown.map((c, i) => (
          <Reveal key={c} delay={(i % 4) * 0.03}>
            <div
              data-testid={`client-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="group flex items-center justify-center text-center border-b border-r border-taupe h-28 md:h-36 px-4 transition-colors duration-300 hover:bg-taupe/40"
            >
              <span className="font-serif text-base md:text-xl text-charcoal/45 group-hover:text-gold transition-colors duration-300">
                {c}
              </span>
            </div>
          </Reveal>
        ))}
      </div>
      {brands.length > INITIAL_VISIBLE && (
        <button
          onClick={() => setOpen(!open)}
          data-testid="brand-toggle"
          className="group mt-6 inline-flex items-center gap-2 font-sans text-xs uppercase tracking-[0.18em] text-charcoal hover:text-gold transition-colors duration-300"
        >
          <span className="relative">
            {open ? "Show less" : `Show all ${brands.length}`}
            <span className="absolute -bottom-1 left-0 w-0 h-px bg-gold transition-all duration-300 group-hover:w-full" />
          </span>
          <span className="transition-transform duration-300 group-hover:translate-x-1">{open ? "↑" : "→"}</span>
        </button>
      )}
    </>
  );
}

export default function Clients() {
  return (
    <>
      <section className="bg-ivory pt-36 md:pt-48 pb-8" data-testid="clients-hero">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20">
          <Reveal><Eyebrow>Our Clients</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-6 font-serif font-light text-charcoal leading-[0.98] tracking-tight text-6xl md:text-8xl lg:text-[8vw]">
              The Roster
            </h1>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-8 max-w-2xl font-sans text-base md:text-lg text-charcoal/65 leading-[1.75] font-light">
              Over 70 fashion, beauty and lifestyle brands scaled across India, the UK, the USA and the Middle East.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-ivory py-16 md:py-24" data-testid="clients-grid">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20">
          {/* Built From Zero highlight */}
          <Reveal>
            <div className="mb-20 md:mb-28 border border-gold/40 rounded-sm p-8 md:p-12" data-testid="built-from-zero-block">
              <Eyebrow>Built From Zero</Eyebrow>
              <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-px bg-gold/20 border border-gold/20">
                {BUILT_FROM_ZERO_CLIENTS.map((c) => (
                  <div
                    key={c}
                    data-testid={`bfz-client-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="group flex items-center justify-center text-center bg-ivory h-28 md:h-36 px-4 transition-colors duration-300 hover:bg-taupe/30"
                  >
                    <span className="font-serif text-lg md:text-2xl text-charcoal/70 group-hover:text-gold transition-colors duration-300">
                      {c}
                    </span>
                  </div>
                ))}
              </div>
              <p className="mt-6 font-sans text-sm text-charcoal/60 italic">
                Brands we launched from zero sales.
              </p>
            </div>
          </Reveal>

          {/* Categorized roster */}
          {CLIENT_CATEGORIES.map((cat, ci) => (
            <div key={cat.label} className={ci > 0 ? "mt-16 md:mt-20 pt-16 md:pt-20 border-t border-gold/30" : ""}>
              <Reveal><Eyebrow>{cat.label}</Eyebrow></Reveal>
              <BrandGrid brands={cat.brands} />
            </div>
          ))}

          {/* Middle East & International roster */}
          <div className="mt-20 md:mt-28 pt-16 md:pt-20 border-t border-gold/30" data-testid="middle-east-roster">
            <Reveal>
              <Eyebrow>Middle East & International</Eyebrow>
              <h2 className="mt-6 font-serif font-light text-charcoal text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-3xl">
                Across the Gulf and beyond.
              </h2>
            </Reveal>
            {MIDDLE_EAST_CATEGORIES.map((cat, ci) => (
              <div key={cat.label} className={ci > 0 ? "mt-14 md:mt-16 pt-14 md:pt-16 border-t border-taupe" : "mt-12 md:mt-16"}>
                <Reveal>
                  <p className="font-sans text-xs uppercase tracking-[0.22em] text-gold mb-8">{cat.label}</p>
                </Reveal>
                <BrandGrid brands={cat.brands} />
              </div>
            ))}
          </div>

          <div className="mt-20 md:mt-28 pt-16 md:pt-20 border-t border-gold/30" data-testid="featured-in">
            <Reveal>
              <p className="font-sans text-xs uppercase tracking-[0.22em] text-gold mb-5">As Featured In</p>
              <p className="font-serif text-xl md:text-2xl text-charcoal/60 leading-relaxed">
                {FEATURED_IN.magazines.join("  ·  ")}
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-10 font-sans text-xs uppercase tracking-[0.22em] text-gold mb-5">Retail Partners & Stockists</p>
              <p className="font-serif text-xl md:text-2xl text-charcoal/60 leading-relaxed">
                {FEATURED_IN.stockists.join("  ·  ")}
              </p>
            </Reveal>
          </div>

          <Reveal>
            <p className="mt-16 max-w-2xl font-sans text-base md:text-lg text-charcoal/65 leading-[1.75] font-light">
              Trusted by fashion, beauty and luxury brands across India, the UK, the USA and the Middle East.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-ivory" data-testid="clients-video-testimonials">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 pb-16 md:pb-24">
          <Reveal><Eyebrow>Founders On Connect.Co</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 mb-14 font-serif font-light text-charcoal text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-3xl">
              Heard from the founders themselves.
            </h2>
          </Reveal>
          <div className={VIDEO_TESTIMONIALS.length > 1 ? "grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12" : "max-w-2xl"}>
            {VIDEO_TESTIMONIALS.map((v, i) => (
              <Reveal key={v.brand} delay={i * 0.1}>
                <VideoTestimonial item={v} testId={`video-testimonial-${v.brand.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-night text-ivory" data-testid="clients-testimonials">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-24 md:py-36">
          <Reveal><Eyebrow>In Their Words</Eyebrow></Reveal>
          <div className="mt-16 space-y-24 md:space-y-32">
            {TESTIMONIALS.map((t, i) => {
              const label = t.name || t.brand;
              const initials = label.split(/[\s&]+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
              const flip = i % 2 === 1;
              return (
                <Reveal key={i} delay={0.05}>
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-14 items-center">
                    <div className={`md:col-span-4 ${flip ? "md:order-2 md:col-start-9" : ""}`}>
                      {t.photo ? (
                        <div className="relative overflow-hidden rounded-md bg-taupe/10 aspect-[4/5] max-w-[280px] mx-auto md:mx-0 ring-1 ring-gold/30">
                          <img
                            src={t.photo}
                            alt={label}
                            data-testid={`founder-photo-${i}`}
                            className="w-full h-full object-cover object-top"
                          />
                        </div>
                      ) : (
                        <div
                          data-testid={`founder-photo-${i}`}
                          data-founder-photo={label}
                          className="relative overflow-hidden rounded-md bg-taupe/10 aspect-[4/5] max-w-[280px] mx-auto md:mx-0 ring-1 ring-gold/40 flex flex-col items-center justify-center text-center"
                        >
                          <span className="font-serif text-5xl md:text-6xl text-gold">{initials}</span>
                          <span className="mt-3 font-sans text-[10px] uppercase tracking-[0.2em] text-ivory/40">Founder photo</span>
                        </div>
                      )}
                    </div>
                    <div className={`md:col-span-8 ${flip ? "md:order-1 md:col-start-1 md:row-start-1" : ""}`}>
                      <blockquote className="font-serif font-light text-ivory text-xl md:text-2xl lg:text-[1.7rem] leading-[1.5]">
                        “{t.quote}”
                      </blockquote>
                      <p className="mt-6 font-sans text-xs uppercase tracking-[0.22em] text-gold">
                        {t.name ? `${t.name} — ${t.brand}` : t.brand}
                      </p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <CTABand title="Join the brands scaling with Connect.Co." variant="taupe" />
    </>
  );
}
